
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { PurchaseOrderModel } from '@/models/purchase_order';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import {
  buildTaxApplyPayload,
  canSubmitTax,
  currentTotalTax,
  defaultTaxSelection,
  extractApiErrorMessage,
  previewTotalTax,
} from './poTaxApply';
import type { TaxApplyLine, TaxSelection, TaxTypeOption } from './poTaxApply';

/**
 * AS-10746 — apply, change or clear the tax on an already-created PO.
 *
 * All the arithmetic and gating live in `poTaxApply.ts` so they are covered by
 * unit tests rather than by a replica of this component's logic.
 */
@Component
export default class ApplyPoTaxDialog extends Vue {
  @Prop({ required: true }) public readonly purchaseOrder!: PurchaseOrderModel;
  @Prop({ default: false }) public readonly open!: boolean;
  @Prop({ default: () => [] }) public readonly taxTypes!: TaxTypeOption[];
  @Prop({ default: null }) public readonly supplierDefaultTaxTypeId!: number | string | null;

  public selectedTaxTypeId: number | null = null;
  public clearTax = false;
  public reason = '';
  public notes = '';
  public saving = false;

  @Watch('open')
  public onOpenChange(next: boolean): void {
    if (next) {
      this.reset();
    }
  }

  public created(): void {
    if (this.open) {
      this.reset();
    }
  }

  public reset(): void {
    this.selectedTaxTypeId = defaultTaxSelection(this.taxTypes, this.supplierDefaultTaxTypeId);
    this.clearTax = false;
    this.reason = '';
    this.notes = '';
  }

  public get lines(): TaxApplyLine[] {
    return ((this.purchaseOrder && (this.purchaseOrder as any).items) || []) as TaxApplyLine[];
  }

  /** null until the form describes something the backend could act on. */
  public get selection(): TaxSelection | null {
    if (this.clearTax) {
      return { clear: true };
    }
    return this.selectedTaxTypeId != null ? { taxTypeId: this.selectedTaxTypeId } : null;
  }

  public get canSubmit(): boolean {
    return !this.saving && canSubmitTax(this.lines, this.selection, this.taxTypes);
  }

  public get currentTax(): number {
    return currentTotalTax(this.lines);
  }

  public get previewTax(): number {
    return this.selection ? previewTotalTax(this.lines, this.selection, this.taxTypes) : this.currentTax;
  }

  public get poCurrencyCode(): string {
    return (this.purchaseOrder && (this.purchaseOrder as any).currencyCode) || 'USD';
  }

  public taxTypeLabel(taxType: TaxTypeOption): string {
    return `${taxType.name} (${(Number(taxType.rate) * 100).toFixed(1)}%)`;
  }

  public formatMoney(value: number): string {
    const n = Number(value);
    return CurrencyHelper.formatCurrencyWithCode(Number.isFinite(n) ? n : 0, this.poCurrencyCode);
  }

  public onDialogInput(value: boolean): void {
    if (!value) {
      this.close();
    }
  }

  public close(): void {
    if (this.saving) {
      return;
    }
    this.$emit('close');
  }

  public async submit(): Promise<void> {
    if (!this.canSubmit || !this.selection) {
      return;
    }
    this.saving = true;
    try {
      const result = await this.purchaseOrder.applyTax(
        buildTaxApplyPayload(this.selection, this.reason, this.notes),
      );
      this.$emit('saved', result);
      // Emitted directly: close() is guarded on `saving`, which is still true
      // until the finally clears it. That guard blocks a user-initiated
      // Cancel mid-save, not the programmatic success dismiss.
      this.$emit('close');
    } catch (err: any) {
      this.$emit('error', extractApiErrorMessage(err, 'Failed to apply tax'));
    } finally {
      this.saving = false;
    }
  }
}
